/**
 * 相机导演（P0-5）：把"取景机位"的工程数据施加到真实视口相机。
 *
 * 语义（与用户裁定的单选制一致）：
 *  · 取景机位变化 / 机位参数被编辑 / 跟随角色移动 → 相机与 OrbitControls 目标重对齐；
 *  · 重对齐后用户仍可自由环绕微调（预设是起点不是枷锁）；微调不回写工程数据——
 *    "记录当前视图"按钮才回写，避免手滑动一下鼠标就改了机位；
 *  · 只在跟随目标角色的 transform 变化时因角色而重对齐：拖动其它对象不打断当前取景。
 */
import { useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import type { PerspectiveCamera } from 'three';

import { resolveTargetPoint } from './cameraMath';
import { viewProbe } from './viewProbe';
import { useDirectorStore } from './store';

/** three-stdlib OrbitControls 的结构化最小依赖面，避免引三方类型包 */
interface OrbitLike {
  target: {
    x: number;
    y: number;
    z: number;
    set: (x: number, y: number, z: number) => void;
  };
  update: () => void;
}

export function CameraDirector() {
  const camera = useThree((s) => s.camera);
  const controls = useThree((s) => s.controls) as unknown as OrbitLike | null;
  const activeId = useDirectorStore((s) => s.project.activeCameraId);
  const viewMode = useDirectorStore((s) => s.viewMode);
  const activeCam = useDirectorStore((s) =>
    s.project.cameras.find((c) => c.id === s.project.activeCameraId),
  );
  const followTransform = useDirectorStore((s) => {
    const c = s.project.cameras.find((cc) => cc.id === s.project.activeCameraId);
    if (c === undefined || c.target.mode !== 'follow' || c.target.followId === null) {
      return null;
    }
    return s.project.objects.find((o) => o.id === c.target.followId)?.transform ?? null;
  });

  // 视图探针注册：页面"记录当前视图"从这里取实时相机态
  useEffect(() => {
    viewProbe.get = () => {
      if (controls === null) {
        return null;
      }
      const cam = camera as PerspectiveCamera;
      return {
        px: camera.position.x,
        py: camera.position.y,
        pz: camera.position.z,
        tx: controls.target.x,
        ty: controls.target.y,
        tz: controls.target.z,
        fov: cam.fov,
      };
    };
    return () => {
      viewProbe.get = () => null;
    };
  }, [camera, controls]);

  useEffect(() => {
    // 只在"相机视角"下把视口相机对齐到取景机位。自由视角下视口相机属 OrbitControls，
    // 若此处仍施加，拖机位 gizmo 改 position 会反过来搬走"用户的眼睛"→无限跳远（第八轮实测）。
    if (viewMode !== 'camera' || activeCam === undefined || controls === null) {
      return;
    }
    const project = useDirectorStore.getState().project;
    const target = resolveTargetPoint(project, activeCam);
    if (target === null) {
      return;
    }
    camera.position.set(activeCam.position[0], activeCam.position[1], activeCam.position[2]);
    controls.target.set(target[0], target[1], target[2]);
    const cam = camera as PerspectiveCamera;
    cam.fov = activeCam.fov;
    cam.updateProjectionMatrix();
    controls.update();
  }, [viewMode, activeId, activeCam, followTransform, camera, controls]);

  return null;
}
